<template>
	<div class="allocation" :class="{ minimised }">
		<tabs :options="{ useUrlFragment: false }" @changed="tabChanged">
			<tab id="people" name="People" :suffix="`<span>${unallocatedPeople.length}</span>`">
				<div v-if="selectedTabId == 'people'">
					<div class="filters">
						<div class="row">
							<div class="col-md-3">
								<InputText id="search" name="" :form="filter.people" :errors="{}" placeholder="Search People"/>
							</div>
						</div>
					</div>
					<Draggable :v-model="[]" group="people" @end="onPersonDrop" class="draggable" :disabled="board.published">
						<Person :person="person" v-for="person in unallocatedPeople" :key="person.id" :data-id="person.id"/>
					</Draggable>
				</div>
			</tab>
			<tab id="horses" name="Horses" :suffix="`<span>${unallocatedHorses.length}</span>`">
				<div v-if="selectedTabId == 'horses'">
					<div class="filters">
						<div class="row">
							<div class="col-md-3">
								<InputText id="search" name="" :form="filter.horses" :errors="{}" placeholder="Search Horses"/>
							</div>
							<div class="col-md-3">
								<InputSelect id="year" name="" :options="ages" :form="filter.horses" :errors="{}"/>
							</div>
							<div class="col-md-3" v-if="tenant && tenant.hasMultiYard">
								<InputSelect id="id" name="" :options="locations" :form="filter.locations" :errors="{}"/>
							</div>
						</div>
					</div>
					<Draggable :v-model="[]" group="horses" @end="onHorseDrop" class="draggable" :disabled="board.published">
						<Horse :horse="horse" v-for="horse in unallocatedHorses" :key="horse.id" :data-id="horse.id"/>
					</Draggable>
				</div>
			</tab>
			<tab class="legend" name="Key">
				<div class="row">
					<div class="col-6">
						<h3>Age</h3>
						<AgeLegend/>
					</div>
					<div class="col-6">
						<h3>Tasks</h3>
						<div class="row">
							<div v-for="(task, index) in tasks" :key="index" class="col-6 tasks">
								<span class="abbreviation">{{task.abbreviation}}</span>
								<span>{{ task.name}}</span>
							</div>
						</div>
					</div>
				</div>
			</tab>
		</tabs>
		<button class="toggle" @click="$emit('toggle')">{{ minimised ? 'Show' : 'Hide' }}</button>
	</div>
</template>

<script>
import { mapState } from 'vuex'
import Draggable from 'vuedraggable'
import Horse from '../Common/Horse.vue'
import Person from '../Common/Person.vue'
import InputText from '../Form/InputText.vue'
import InputSelect from '../Form/InputSelect.vue'
import AgeLegend from './AgeLegend.vue'

export default {
	props: ['board', 'filter', 'columns', 'minimised'],
	components: {
		Draggable,
		Horse,
		Person,
		InputText,
		InputSelect,
		AgeLegend
	},
	data() {
		return {
			selectedTabId: ''
		}
	},
	computed: {
		...mapState({
			people: state => state.people.list,
			horseList: state => state.horses.list,
			tasks: state => state.tasks.list,
		}),
		peopleById() {
			return this.mapArrayById(this.$store.getters['people/list'], 'id')
		},
		horsesById() {
			return this.mapArrayById(this.$store.getters['horses/list'], 'id')
		},
		ages() {
			let ages = [{ key: 0, value: 'All Ages' }]
	
			 this.$store.getters['age/list'].forEach(age => {
				ages.push({
					key: age.year,
					value: age.text
				})
			})

			return ages
		},
		unallocatedPeople() {
			return this.people.filter(person => {
				return !this.board.allocated.people.includes(person.id)
				&& (this.filter.people.search.toLowerCase().trim() === '' || person.name.toLowerCase().includes(this.filter.people.search.toLowerCase().trim()))
			})
		},
		unallocatedHorses() {
			return this.horses.filter(horse => {
				return !this.board.allocated.horses.includes(horse.id)
					&& horse.is_away != 1
					&& (this.filter.horses.search.toLowerCase().trim() === '' || horse.name.toLowerCase().includes(this.filter.horses.search.toLowerCase().trim()))
					&& (this.filter.horses.year === 0 || horse.age_year === this.filter.horses.year)
			})
		},
		columnStyle() {
			const columnWidth = 100 / this.columns

			return {
				width: columnWidth + '%'
			}
		},
		blankPeopleContainers() {
			return this.columns * 5
		},
		blankHorseContainers() {
			return this.columns * 5
		},
		tenant() {
			return this.$store.getters['tenant/get']
		},
		locations() {
			let locations = [{ key: 0, value: 'All Locations' }]
	
			 this.$store.getters['locations/list'].forEach(location => {
				locations.push({
					key: location.id,
					value: location.name
				})
			})

			return locations
		},
		horses() {
			return this.horseList.filter(horse => {
				if(this.tenant && this.tenant.hasMultiYard) {
					return this.filter.locations.id === 0 || horse.location_id === this.filter.locations.id	
				}
				return horse
			})
		}
	},
	mounted() {
		this.$store.commit('app/setLoading', true)
		const tasks = this.$store.dispatch('tasks/getList')

		Promise.all([tasks])
		.then(() => {
			this.$store.commit('app/setLoading', false);
		})
		.catch(err => {
			this.$store.commit('app/setLoading', false);
			this.$store.dispatch('app/addAlert', {
				type: 'error',
				message: 'An Error occurred, please try later',
				timeout: 3000
			})
		})
	},
	methods: {
		onPersonDrop(move) {
			if(move.from.dataset.row !== move.to.dataset.row) {
				const sourcePerson =  move.item.dataset.id
				const currentTargetRow = this.board.rows[move.to.dataset.row]
				let currentTargetPerson = undefined

				if(currentTargetRow && currentTargetRow.person) {
					currentTargetPerson = currentTargetRow.person
				}

				//request changes
				let changes = []

				if(move.to.dataset.row) {
					//Dropped on board
					changes.push({
						type: 'ASSIGN_PERSON',
						entity: sourcePerson,
						target: { row: move.to.dataset.row }
					})
				} else {
					//Dropped in unallocated
					changes.push({
						type: 'UNASSIGN_PERSON',
						entity: sourcePerson,
					})
				}

				if(currentTargetPerson) {
					if(!move.from.dataset.row) {
						//Person from unallocated tab
						changes.push({
							type: 'UNASSIGN_PERSON',
							entity: currentTargetPerson
						})
					} else {
						//swap positions
						changes.push({
							type: 'ASSIGN_PERSON',
							entity: currentTargetPerson,
							target: { row: move.from.dataset.row }
						})
					}
				} else if(move.from.dataset.row) {
					//Clear where horse came from
					changes.push({
						type: 'CLEAR_PERSON',
						target: { row: move.from.dataset.row }
					})
				}

				this.$emit('change', changes)
			}
		},
		onHorseDrop(move) {
			if(move.from.dataset.row !== move.to.dataset.row || move.from.dataset.lot !== move.to.dataset.lot) {
				const sourceHorse = move.item.dataset.id
				const currentTargetRow = this.board.rows[move.to.dataset.row]
				let currentTargetHorse = undefined

				if(currentTargetRow && currentTargetRow.lots[move.to.dataset.lot]) {
					currentTargetHorse = currentTargetRow.lots[move.to.dataset.lot]
				}

				//request changes
				let changes = []

				if(move.to.dataset.row) {
					changes.push({
						type: 'ASSIGN_HORSE',
						entity: sourceHorse,
						target: { row: move.to.dataset.row, lot: move.to.dataset.lot }
					})
				} else {
					changes.push({
						type: 'UNASSIGN_HORSE',
						entity: sourceHorse,
					})
				}

				if(currentTargetHorse) {
					if(!move.from.dataset.row) {
						//Horse from unallocated tab
						changes.push({
							type: 'UNASSIGN_HORSE',
							entity: currentTargetHorse
						})
					} else {
						//swap positions
						changes.push({
							type: 'ASSIGN_HORSE',
							entity: currentTargetHorse,
							target: { row: move.from.dataset.row, lot: move.from.dataset.lot }
						})
					}
				} else if(move.from.dataset.row) {
					//Clear where horse came from
					changes.push({
						type: 'CLEAR_HORSE',
						target: { row: move.from.dataset.row, lot: move.from.dataset.lot }
					})
				}

				this.$emit('change', changes)
			}
		},
		tabChanged(selectedTab) {
			this.selectedTabId = selectedTab.tab.id
		}
	}
}
</script>

<style lang="scss">
@import "../../scss/mixins.scss";
@import "../../scss/variables.scss";

.allocation {
	position: fixed;
	bottom: 0;
	left: 0;
	right: 0;
	height: 249px;
	transition: height .5s ease-in-out;
	z-index: 2;
	.toggle {
		position: absolute;
		right: 30px;
		top: 55px;
		color: #fff;
		height: 18px;
		width: 18px;
		padding: 0;
		background: transparent;
		border: none;
		span {
			display: block;
			width: 17px;
			border-top: 2px solid #fff;
		}
	}
	.tabs-component-tabs {
		transition: margin .5s ease-in-out;
		margin-left: 45px;
	}
	.tabs-component-panels {
		overflow-y: scroll;
		height: 200px;
		padding-top: 25px;
		padding-left: 45px;
		padding-right: 45px;
		background-color: rgba(#0C2D39, 0.9);
		-webkit-border-top-left-radius: 8px;
		-webkit-border-top-right-radius: 8px;
		-moz-border-radius-topleft: 8px;
		-moz-border-radius-topright: 8px;
		border-top-left-radius: 8px;
		border-top-right-radius: 8px;
	}

	.tabs-component-tabs {
		border: 0;
		align-items: stretch;
		display: flex;
		justify-content: flex-start;
		margin-bottom: 0px;
	}

	.tabs-component-tab {
		font-size: 14px;
		line-height: 35px;
		list-style: none;
		margin-left: 5px;
	}

	.tabs-component-tab.is-active {
		z-index: 2;
		.tabs-component-tab-a {
			background: rgba(#0C2D39, 0.9);
			border: 1px solid rgba(#0C2D39, 0.9);
			color: #fff;
			span {
				background: #fff;
				color: #0C2D39;
			}
		}
	}

	.tabs-component-tab-a {
		position: relative;
		align-items: center;
		color: inherit;
		display: flex;
		border: 1px solid #E2E3E4;
		-webkit-border-top-left-radius: 4px;
		-webkit-border-top-right-radius: 4px;
		-moz-border-radius-topleft: 4px;
		-moz-border-radius-topright: 4px;
		border-top-left-radius: 4px;
		border-top-right-radius: 4px;
		height: 35px;
		width: 140px;
		text-decoration: none;
		display: block;
		text-align: center;
		background: #fff;
		span {
			display: block;
			position: absolute;
			top: 7px;
			right: 7px;
			padding: 5px;
			font-size: 10px;
			line-height: 10px;
			background: #E2E3E4;
			@include border-radius(2px);
		}
	}
	.filters {
		margin-top: 5px;
		padding-left: 5px;
	}
	.bar.horse, .bar.person {
		cursor: pointer;
		display: inline-block;
		margin: 5px;
		width: calc(20% - 10px);
	}
	.bar-wrapper {
		display: inline-block;
		padding: 5px;
	}
	.legend {
		color: white;
		.tasks {
			margin-bottom: 20px;
			.abbreviation {
				text-align: center;
				font-weight: 600;
				display: inline-block;
				color: black;
				border-radius: 3px;
				width: 40px;
				background-color: $grey-button;
			}
		}
	}
}

.allocation.minimised {
	height: 79px;
	.tabs-component-panels {
		overflow: hidden;
	}
}
</style>


// WEBPACK FOOTER //
// src/components/Board/AllocationTabs.vue